// Shared postMessage protocol between the main app (parent window) and an embedded MFE (iframe).
// Both the main app and the MFE host app import these definitions so the message shapes stay in
// sync. Every message is an MfeMessage: a `type` discriminator plus that message's payload. A
// window "message" listener hands the event to dispatchMfeMessage with a map of handlers keyed by
// message type; messages with no handler fall through untouched.

export const mfeReadyMessageType = "datafabriq-mfe-ready" as const;
export const mfeTokenMessageType = "datafabriq-mfe-token" as const;
export const mfeNavigateMessageType = "datafabriq-mfe-navigate" as const;
export const mfeDownloadMessageType = "datafabriq-mfe-download" as const;

/**
 * Posted by the MFE bootstrap (running inside the iframe) to its parent once it is mounted
 * and ready to receive the read-only token. The parent does not send the token until it has
 * received this, which avoids a race with the iframe's script load.
 */
export interface MfeReadyMessage {
  type: typeof mfeReadyMessageType;
}

/**
 * Posted by the parent (main app) to the MFE iframe, handing over the short-lived read-only
 * app JWT. The MFE then POSTs this token to `/api/v1/auth/mfe-session` to establish a
 * host-only read-only session on its own origin.
 *
 * `clientInstanceId` is the parent document's data-change echo-suppression id; the iframe adopts it
 * before its first API call so the parent and the iframes it hosts count as one client (see
 * clientInstanceId.ts). Optional so a parent bundle from before the field existed still hands the
 * token over.
 */
export interface MfeTokenMessage {
  type: typeof mfeTokenMessageType;
  token: string;
  clientInstanceId?: string;
}

/**
 * Posted by the parent (main app) to the MFE iframe to switch the embedded view without reloading
 * the iframe. The iframe is loaded once; subsequent view changes are sent as navigate messages so
 * the ops app's React tree, read-only session, and data cache stay warm across navigations. The
 * path is a route only (no token), so it carries nothing sensitive.
 */
export interface MfeNavigateMessage {
  type: typeof mfeNavigateMessageType;
  path: string;
}

/**
 * Posted by an embedded MFE to its parent asking the top-level document — which holds the
 * full-scope session and is not sandboxed — to download an ops app document. Carries only the
 * document id, an opaque server-issued string; the parent builds the cookie-authenticated URL
 * itself.
 */
export interface MfeDownloadMessage {
  type: typeof mfeDownloadMessageType;
  documentId: string;
}

/** Every message exchanged between the parent app and an embedded MFE. */
export type MfeMessage = MfeReadyMessage | MfeTokenMessage | MfeNavigateMessage | MfeDownloadMessage;

/**
 * Handlers keyed by message type. Each handler receives the message narrowed to its own shape plus
 * the originating event (for callers that need the sender or a reply channel). A listener supplies
 * handlers only for the message types it serves; every other message falls through.
 */
export type MfeMessageHandlers = {
  [TMessage in MfeMessage as TMessage["type"]]?: (message: TMessage, event: MessageEvent) => void;
};

type AnyMfeMessageHandler = (message: MfeMessage, event: MessageEvent) => void;

/**
 * Routes a window "message" event to the handler registered for its message type. Events whose
 * data is not an MfeMessage, or whose type has no handler, are ignored. Callers decide who they
 * trust (origin, source window) before dispatching; this only routes.
 */
export function dispatchMfeMessage(event: MessageEvent, handlers: MfeMessageHandlers): void {
  const data: unknown = event.data;

  if (typeof data !== "object" || data === null || typeof (data as { type?: unknown }).type !== "string") {
    return;
  }

  const message = data as MfeMessage;

  // Own-property lookup so a message type that happens to name something on Object.prototype
  // (e.g. "constructor") is not mistaken for a registered handler.
  if (!Object.prototype.hasOwnProperty.call(handlers, message.type)) {
    return;
  }

  const handler = handlers[message.type] as AnyMfeMessageHandler | undefined;

  if (handler === undefined) {
    return;
  }

  handler(message, event);
}
